import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';

import { useAppDispatch } from '@/app/store/hooks';
import { SignaturePicker, type SignaturePickerHandle } from '@/features/account';
import {
  useCreateSignatureMutation,
  useGetSignaturesQuery,
  useMarkDocumentViewedMutation,
} from '@/features/annual-review';
import { useSignDocumentMutation } from '@/features/forms';
import { toastShown } from '@/features/ui';

import { signableDocumentIdOf, titleOf } from './outstanding';
import type { OutstandingItem } from './outstanding.schema';
import { SignableDocument } from './SignableDocument';
import { signableDocumentUrlOf } from './signableDocumentUrl';
import { SignDeclaration } from './SignDeclaration';
import { signOutcome } from './signOutcome';

interface SignItemDialogProps {
  item: OutstandingItem;
  onClose: () => void;
}

/**
 * Signs a document.
 *
 * The signature applied is the row's *signable* document — for an annual review
 * that is the solvency resolution, not the row's own id. Getting that wrong
 * signs the wrong document, which is why it is derived rather than passed in.
 *
 * A signature can be picked from the saved ones, drawn, or typed. Offering only
 * saved ones sent a first-time director to Settings and back mid-task; the
 * picker is shared with Settings, so there is still exactly one canvas in the
 * app.
 */
export const SignItemDialog = ({ item, onClose }: SignItemDialogProps) => {
  const dispatch = useAppDispatch();
  const { data: signatures = [], isLoading } = useGetSignaturesQuery(undefined);
  const [signDocument] = useSignDocumentMutation();
  const [createSignature] = useCreateSignatureMutation();
  const [markDocumentViewed] = useMarkDocumentViewedMutation();

  const pickerRef = useRef<SignaturePickerHandle>(null);
  // Seeded false and driven entirely by the picker. Deriving it from
  // `signatures.length` here looks equivalent but is not: that list is empty on
  // the first render while the request is in flight, and `useState` ignores
  // every later argument — so a director with a saved signature was left with a
  // permanently disabled button. The picker knows what it has selected, empty
  // tabs included, so it is the only thing that should decide this.
  const [isReady, setIsReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [saveSignature, setSaveSignature] = useState(false);
  const [hasViewedDocuments, setHasViewedDocuments] = useState(false);
  // The Saved tab is the default when the director has one, so this has to be
  // asked of the picker rather than assumed — the checkbox would otherwise
  // offer to re-save a signature that is already in their list.
  const [isNewSignature, setIsNewSignature] = useState(true);

  const handleSubmit = async () => {
    const resolved = pickerRef.current?.resolve() ?? null;
    if (resolved === null) {
      setFormError('That signature could not be prepared. Try drawing a simpler one.');
      return;
    }

    setIsSubmitting(true);
    setFormError('');

    const documentId = signableDocumentIdOf(item);

    try {
      await signDocument({ documentId, signature: resolved.image }).unwrap();
    } catch {
      setFormError('That document could not be signed. Please try again.');
      setIsSubmitting(false);
      return;
    }

    // Both of these are recorded AFTER the signature lands and neither can undo
    // it, so a failure here must not be reported as a failure to sign. Saving
    // is surfaced because the director asked for it and would otherwise find
    // the signature missing next time; the viewed flag is bookkeeping they
    // never see, so a failure there is not worth interrupting them over.
    const wantsSave = saveSignature && resolved.isNew;
    let saved = wantsSave;

    if (wantsSave) {
      try {
        await createSignature({ signature: resolved.image }).unwrap();
      } catch {
        saved = false;
      }
    }

    if (hasViewedDocuments) {
      await markDocumentViewed({ asicCompanyId: item.asicCompanyId, documentId });
    }

    const outcome = signOutcome(wantsSave, saved);
    dispatch(toastShown(outcome.message, outcome.severity));
    onClose();
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Sign {titleOf(item)}</DialogTitle>

      <DialogContent dividers className="flex flex-col gap-4">
        {formError !== '' && (
          <Alert severity="error" role="alert" className="rounded-lg">
            {formError}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary">
          {item.asicCompanyName}
        </Typography>

        {/* First, above the signature: the order the decision is actually made
            in. Reading opens the app's shared document viewer rather than a
            frame inside this dialog — see `SignableDocument`. */}
        <SignableDocument href={signableDocumentUrlOf(item)} title={titleOf(item)} />

        <SignaturePicker
          ref={pickerRef}
          savedSignatures={signatures}
          isLoadingSaved={isLoading}
          onReadyChange={setIsReady}
          onIsNewChange={setIsNewSignature}
        />

        <SignDeclaration
          canSave={isNewSignature}
          saveSignature={saveSignature}
          onSaveSignatureChange={setSaveSignature}
          hasViewedDocuments={hasViewedDocuments}
          onHasViewedDocumentsChange={setHasViewedDocuments}
        />

        <Typography variant="caption" color="text.secondary">
          Applying your signature is legally binding.
        </Typography>
      </DialogContent>

      <DialogActions className="flex-col-reverse gap-2 p-4 sm:flex-row sm:justify-end">
        <Button onClick={onClose} disabled={isSubmitting} className="w-full sm:w-auto">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={!isReady || !hasViewedDocuments || isSubmitting}
          className="w-full sm:w-auto"
        >
          {isSubmitting ? 'Signing…' : 'Sign document'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
